/**
 * Offer alerting under browser restrictions.
 *
 * Browsers block audio until the user interacts with the page (autoplay policy), so:
 *  1. The AudioContext is created/resumed inside a user gesture (login, "Conectarme", "Probar sonido").
 *  2. If audio is still locked when an offer arrives, we fall back to: vibration, a system
 *     notification (the OS plays its own sound), a flashing title and an on-screen "tap to enable sound".
 *  3. Wake Lock keeps the screen on while the driver is available, so the page is not throttled.
 *  4. Android shell (apps/driver-android): no service worker there, so the system notification is a
 *     native local one on a high-importance channel; tapping it brings the app (and the offer) up.
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

export type Tone = 'classic' | 'alarm' | 'soft';

const nativeNotify = Capacitor.isPluginAvailable('LocalNotifications');
/** One alert at a time: a new offer or call replaces the notification of the previous one. */
const NATIVE_ALERT_ID = 1001;
let alertChannel: Promise<unknown> | null = null;
/** Browser: tag of the system notification of the alert in course, to take it down when the alert ends. */
let shownTag: string | null = null;

function showNativeAlert(title: string, body: string) {
  alertChannel ??= LocalNotifications.createChannel({ id: 'offers', name: 'Ofertas y llamadas', description: 'Servicios ofrecidos y llamadas de la central', importance: 5, visibility: 1, vibration: true }).catch(() => undefined);
  void alertChannel
    .then(() => LocalNotifications.schedule({ notifications: [{ id: NATIVE_ALERT_ID, title, body, channelId: 'offers', autoCancel: true, isExactNotification: false }] }))
    .catch(() => undefined);
}

let ctx: AudioContext | null = null;
const timers: Array<ReturnType<typeof setInterval>> = [];
let wakeLock: { release: () => Promise<void> } | null = null;
const originalTitle = document.title;

export function audioUnlocked(): boolean {
  return !!ctx && ctx.state === 'running';
}

/** Must be called from a user gesture handler. */
export async function unlockAudio(): Promise<boolean> {
  try {
    ctx ??= new AudioContext();
    if (ctx.state !== 'running') await ctx.resume();
    // a silent blip fully unlocks iOS Safari
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.value = 0;
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.01);
    return ctx.state === 'running';
  } catch {
    return false;
  }
}

function beep(freq: number, start: number, dur: number, volume: number) {
  if (!ctx) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'square';
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
  g.gain.exponentialRampToValueAtTime(Math.max(0.001, volume), ctx.currentTime + start + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
  o.connect(g).connect(ctx.destination);
  o.start(ctx.currentTime + start);
  o.stop(ctx.currentTime + start + dur + 0.02);
}

const PATTERNS: Record<Tone, Array<[number, number, number]>> = {
  classic: [
    [880, 0, 0.18],
    [1175, 0.22, 0.18],
    [880, 0.44, 0.18],
    [1175, 0.66, 0.3],
  ],
  alarm: [
    [1400, 0, 0.12],
    [1400, 0.16, 0.12],
    [1400, 0.32, 0.12],
    [1000, 0.52, 0.3],
  ],
  soft: [
    [660, 0, 0.25],
    [880, 0.3, 0.35],
  ],
};

export function playOnce(tone: Tone = 'classic', volumePct = 100) {
  const v = (volumePct / 100) * 0.35;
  for (const [f, s, d] of PATTERNS[tone]) beep(f, s, d, v);
}

export interface AlertOptions {
  sound: boolean;
  tone: Tone;
  volume: number;
  vibrate: boolean;
  title: string;
  body: string;
  tag: string;
}

/** Starts a repeating alert until stopAlert() is called. Returns which channels actually worked. */
export function startAlert(o: AlertOptions): { sound: boolean; vibration: boolean; systemNotification: boolean } {
  stopAlert();
  let sound = false;
  if (o.sound && audioUnlocked()) {
    playOnce(o.tone, o.volume);
    timers.push(setInterval(() => playOnce(o.tone, o.volume), 1800));
    sound = true;
  }
  let vibration = false;
  if (o.vibrate && 'vibrate' in navigator) {
    vibration = navigator.vibrate([500, 200, 500, 200, 800]);
    timers.push(setInterval(() => navigator.vibrate?.([500, 200, 500]), 2500));
  }
  timers.push(setInterval(() => (document.title = document.title === originalTitle ? `🔔 ${o.title}` : originalTitle), 900));
  // Fallback: system notification when audio is locked or the tab is hidden (the OS plays its own sound).
  let systemNotification = false;
  if (nativeNotify) {
    const needed = () => !sound || document.visibilityState !== 'visible';
    if (needed()) {
      showNativeAlert(o.title, o.body);
      systemNotification = true;
    }
    // In the background the WebView's own sound is not heard and a notification rings once (the
    // plugin posts it as "alert once"): it is taken down and posted again so the phone keeps
    // ringing until the offer is answered or ends.
    timers.push(
      setInterval(() => {
        if (!needed()) return;
        void LocalNotifications.cancel({ notifications: [{ id: NATIVE_ALERT_ID }] })
          .catch(() => undefined)
          .then(() => showNativeAlert(o.title, o.body));
      }, 4000),
    );
  } else if ((!sound || document.visibilityState !== 'visible') && 'Notification' in window && Notification.permission === 'granted') {
    navigator.serviceWorker?.ready
      .then((reg) =>
        reg.showNotification(o.title, { body: o.body, tag: o.tag, requireInteraction: true, icon: '/icons/icon-192.png', badge: '/icons/badge-72.png' }),
      )
      .catch(() => undefined);
    shownTag = o.tag;
    systemNotification = true;
  }
  return { sound, vibration, systemNotification };
}

export function stopAlert() {
  timers.splice(0).forEach((t) => clearInterval(t));
  document.title = originalTitle;
  navigator.vibrate?.(0);
  if (nativeNotify) void LocalNotifications.cancel({ notifications: [{ id: NATIVE_ALERT_ID }] }).catch(() => undefined);
  // an answered, withdrawn or expired offer (or an ended call) must not stay on the phone's tray
  if (shownTag) {
    const tag = shownTag;
    shownTag = null;
    void navigator.serviceWorker?.ready.then((reg) => reg.getNotifications({ tag })).then((list) => list.forEach((n) => n.close())).catch(() => undefined);
  }
}

export async function keepAwake(on: boolean) {
  try {
    const wl = (navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock;
    if (on && wl && !wakeLock && document.visibilityState === 'visible') wakeLock = await wl.request('screen');
    if (!on && wakeLock) {
      await wakeLock.release();
      wakeLock = null;
    }
  } catch {
    wakeLock = null;
  }
}

document.addEventListener('visibilitychange', () => {
  // the wake lock is released automatically when the page is hidden; the app re-requests it on focus
  if (document.visibilityState === 'hidden') wakeLock = null;
});
